import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { CreditsProvider } from "@/context/CreditsContext";
import { TourProvider } from "@/context/TourContext";
import { ListenerProvider } from "@/context/ListenerContext";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import Studio from "@/pages/Studio";
import Landing from "@/pages/Landing";
import Guide from "@/pages/Guide";
import ListenPage from "@/pages/ListenPage";
import LibraryPage from "@/pages/LibraryPage";
import DiscoverPage from "@/pages/DiscoverPage";
import AdminPage from "@/pages/AdminPage";
import DonationThanks from "@/pages/DonationThanks";
import CreditsThanks from "@/pages/CreditsThanks";
import PwaManager from "@/components/PwaManager";
import { AUDIOBOOKS_ENABLED } from "@/config/features";
import { Loader2 } from "lucide-react";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function Routing() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/guide" element={<Guide />} />
      <Route path="/listen/:shareId" element={AUDIOBOOKS_ENABLED ? <ListenPage /> : <Navigate to="/" replace />} />
      <Route path="/discover" element={AUDIOBOOKS_ENABLED ? <DiscoverPage /> : <Navigate to="/" replace />} />
      <Route path="/library" element={AUDIOBOOKS_ENABLED ? <LibraryPage /> : <Navigate to="/" replace />} />
      <Route path="/donation/thanks" element={<DonationThanks />} />
      <Route path="/credits/thanks" element={<Protected><CreditsThanks /></Protected>} />
      <Route path="/auth" element={user ? <Navigate to="/app" replace /> : <AuthPage />} />
      <Route path="/app" element={<Protected><Dashboard /></Protected>} />
      <Route path="/admin" element={<Protected><AdminPage /></Protected>} />
      <Route path="/project/:projectId" element={<Protected><Studio /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <CreditsProvider>
            <BrowserRouter>
              <ScrollToTop />
              <TourProvider>
                <ListenerProvider>
                  <Routing />
                </ListenerProvider>
              </TourProvider>
            </BrowserRouter>
            <Toaster position="bottom-right" richColors closeButton />
            <PwaManager />
          </CreditsProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
